import React, { useState } from 'react';
import {
  BodyText,
  Button,
  FlexColumn,
  FlexRow,
  Input,
  SubtitleText,
  Textarea
} from '@make-software/cspr-design';
import styled from 'styled-components';
import Big from 'big.js';

import { encrypt, bytesToHex } from '@/utils/encryption';
import { PaymentAmount, TransactionPayment } from '@/components';

export const FormTitle = styled(SubtitleText)(({ theme }) =>
  theme.withMedia({
    fontWeight: [600, 600, 700],
    fontSize: ['20px', '24px', '24px'],
  }),
);

export const StyledInput = styled(Input)<{ withBorder?: boolean }>(({ theme, withBorder }) =>
  theme.withMedia({
    width: '100%',
    'div:nth-child(2)': {
      border: withBorder ? `1px solid ${theme.styleguideColors.contentSecondary}` : 'none'
    }
  })
);

export const StyledTextArea = styled(Textarea)(({ theme }) =>
  theme.withMedia({
    width: '100%'
  })
);

export const EncryptedMessageLabel = styled(BodyText)(({ theme }) =>
  theme.withMedia({
    paddingBottom: 4
  })
);

export const EncryptedMessageContainer = styled(FlexColumn)(({ theme }) =>
  theme.withMedia({
    width: '100%'
  })
);

export const StyledButton = styled(Button)(({ theme }) =>
  theme.withMedia({
    cursor: 'pointer'
  })
);

interface SendMessageFormProps {
  onSubmit: (recipient: string, message: string) => void;
}

export const SendMessageForm: React.FC<SendMessageFormProps> = ({ onSubmit }) => {
  const [recipient, setRecipient] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [encryptedMessage, setEncryptedMessage] = useState<string>('');
  const [formErrors, setFormErrors] = useState<Record<'recipient' | 'message', string>>({
    recipient: '',
    message: ''
  });

  const handleRecipient = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value;

    setFormErrors((prev) => ({ ...prev, recipient: '' }));

    if (val === '') {
      setRecipient(val);
      return;
    }

    if (/^[0-9a-fA-F]*$/.test(val)) {
      if (val.length <= 68) {
        setRecipient(val);
      }
      else {
        setFormErrors((prev) => ({
          ...prev,
          recipient: 'Invalid public key'
        }));
      }
    }
  };

  const handleMessage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value;

    // if (val.length <= 876) {
    if (val.length <= 768) {
      setMessage(val);
      setFormErrors((prev) => ({ ...prev, message: '' }));
    } else {
      setFormErrors((prev) => ({
        ...prev,
        message: 'Max limit reached (768 symbols)'
      }));
    }
  };

  const handleEncrypt = async () => {
    const nextErrors: Record<'recipient' | 'message', string> = { recipient: '', message: '' };

    if (!recipient.length) {
      nextErrors.recipient = 'This field is required';
    }

    if (recipient.length !== 66 && recipient.length !== 68) {
      nextErrors.recipient = 'Invalid public key';
    }

    if (!message.length) {
      nextErrors.message = 'This field is required';
    }

    if (nextErrors.recipient || nextErrors.message) {
      setFormErrors(nextErrors);
      return;
    }

    const encryptedMessage = await encrypt(recipient.slice(2), message);
    setEncryptedMessage(bytesToHex(encryptedMessage));
  };

  const handleSubmit = () => {
    const nextErrors: Record<'recipient' | 'message', string> = { recipient: '', message: '' };

    if (!recipient.length) {
      nextErrors.recipient = 'This field is required';
    }

    if (recipient.length !== 66 && recipient.length !== 68) {
      nextErrors.recipient = 'Invalid public key';
    }

    if (!message.length) {
      nextErrors.message = 'This field is required';
    }

    if (nextErrors.recipient || nextErrors.message) {
      setFormErrors(nextErrors);
      return;
    }

    onSubmit(recipient, message);
  };

  const hasErrors = Boolean(formErrors.recipient || formErrors.message);
  const isIncomplete = !recipient.length || !message.length;

  const transactionPayment = new Big(config.message_transaction_cost)
    .mul(1000000000);

  return (
    <FlexColumn gap={25}>
      <FormTitle size={1} scale='lg' variation='black'>
        Compose Message
      </FormTitle>

      <FlexRow>
        <StyledInput
          value={recipient}
          label={
            <BodyText size={1} variation="black">
              Recipient
            </BodyText>
          }
          placeholder="Public key"
          onChange={handleRecipient}
          error={!!formErrors.recipient}
          validationText={formErrors.recipient}
        />
      </FlexRow>

      <FlexRow>
        {encryptedMessage.length === 0
          ? <StyledTextArea
            value={message}
            label={
              <BodyText size={1} variation="black">
                Message
              </BodyText>
            }
            placeholder="Message text"
            onChange={handleMessage}
            error={!!formErrors.message}
            validationText={formErrors.message}
          />
          : <EncryptedMessageContainer>
              <EncryptedMessageLabel size={1} variation="black">
                Encrypted Message
              </EncryptedMessageLabel>
              <StyledTextArea
                value={encryptedMessage}
                disabled
              />
          </EncryptedMessageContainer>
        }
      </FlexRow>

      {encryptedMessage.length > 0 && <>
        <TransactionPayment
          amount={transactionPayment.toString()}
          canProceed={!isIncomplete}
          withDivider
        />

        <PaymentAmount
          category={`Cost`}
          formattedFiatAmount="1 STAMP"
          canProceed={!isIncomplete}
          withDivider={false}
        />

        <PaymentAmount
          category="Total"
          amount={transactionPayment.toString()}
          formattedFiatAmount="1 STAMP"
          canProceed={!isIncomplete}
          withDivider
        />
      </>}

      <FlexRow>
        {encryptedMessage.length === 0
          ? <StyledButton disabled={hasErrors || isIncomplete} onClick={handleEncrypt}>
            Encrypt
          </StyledButton>
          : <StyledButton  color="primaryRed" disabled={hasErrors || isIncomplete} onClick={handleSubmit}>
            Send
          </StyledButton>
        }
      </FlexRow>
    </FlexColumn>
  );
};
